import React from "react";
import "./ContainerSelect.css";

/**
 * The container selector, shared by every page that has one.
 *
 * ONE COMPONENT, because the rule about what it offers is a decision and a
 * decision that exists in three files eventually becomes three decisions — one
 * page defaulting to All, another hiding test containers, with nothing to say which
 * was meant. The server already decided who may see what; this only renders it.
 *
 * IT NEVER DEFAULTS TO ALL. The server sends `selection` with the default container
 * pre-chosen, and this shows what it was told. A page that opened on every container
 * would open every broker session at once.
 *
 * THE SELECTION IS PER VIEWER. It lives in this page's URL, so one person
 * switching container moves nobody else's view — and a particular container is a link
 * somebody can be sent.
 */
export default function ContainerSelect({
  containers = [],
  value = "",
  onChange,
  includeAll = true,
  label = "Container",
  id = "container-select",
  busy = false,
}) {
  /* Nothing to choose between: one container and no All option is not a choice, and a
     dropdown with a single entry invites a click that does nothing. */
  if (!containers.length || (containers.length === 1 && !includeAll)) return null;

  const current = containers.find((p) => p.id === value);

  return (
    <div className="cs-wrap">
      <label className="cs-label" htmlFor={id}>
        {label}
      </label>
      <div className="cs-field">
        <select
          id={id}
          className="cs-select"
          value={value}
          disabled={busy}
          onChange={(e) => onChange && onChange(e.target.value)}
        >
          {includeAll && <option value="all">All containers</option>}
          {containers.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
              {p.mode === "test" ? "  · test" : ""}
              {p.includeInOverall === false ? "  · not in totals" : ""}
              {p.publicVisible === false ? "  · private" : ""}
            </option>
          ))}
        </select>

        {/* The badges repeat what the option text says, because the option text
            is only visible while the menu is open. A test container sitting on screen
            has to look different from a live one at a glance. */}
        {current && current.mode === "test" && <span className="cs-badge cs-badge-test">test</span>}
        {current && current.includeInOverall === false && (
          <span className="cs-badge cs-badge-out" title="Excluded from the combined totals">
            not in totals
          </span>
        )}
        {current && current.publicVisible === false && (
          <span className="cs-badge cs-badge-private" title="Hidden from anyone not signed in as a developer">
            private
          </span>
        )}
        {value === "all" && (
          <span className="cs-badge cs-badge-all">
            {containers.length} container{containers.length === 1 ? "" : "s"}
          </span>
        )}
      </div>
    </div>
  );
}
